import { Grid, Box, Paper, Skeleton, Stack } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import tripService from "services/tripService";
import moment from "moment";
import { B2BActivityRequest } from "trips/models";
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridSortModel,
  useGridApiRef,
} from "@mui/x-data-grid";

// Mirrors the legacy dashboard's "B2B Trip Modification" grid (ReviewOnTime.aspx GetB2B).
// Renders parsed inbound UPRR / BNSF messages for a trip.
const TripB2BActivity = ({ trip }) => {
  const initialSearch: B2BActivityRequest = {
    offset: 0,
    pageSize: 25,
    sortAsc: false,
    tripId: trip?.id,
  };
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState<B2BActivityRequest>(initialSearch);
  const [total, setTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const gridRef = useGridApiRef();
  // Tracks the last fetched request so spurious setSearch calls (e.g. MUI
  // DataGrid firing onSortModelChange + onPaginationModelChange on mount with
  // values identical to initialState) don't refetch.
  const lastFetchKeyRef = useRef<string | null>(null);

  const fetchActivity = (req: B2BActivityRequest) => {
    setIsLoading(true);
    setRows([]);
    setTotal(0);
    tripService
      .getB2BActivity(req)
      .then((results) => {
        setRows(results.results || []);
        setTotal(results.count || 0);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (!trip?.id) return;
    const req = { ...search, tripId: trip.id };
    const key = JSON.stringify(req);
    if (key === lastFetchKeyRef.current) return;
    lastFetchKeyRef.current = key;
    fetchActivity(req);
  }, [trip?.id, search]);

  const columns: GridColDef[] = [
    {
      field: "timeStamp",
      headerName: "Received",
      width: 180,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <>{params.value ? moment.utc(params.value).local().format("MM/DD/YYYY HH:mm") : ""}</>
      ),
    },
    { field: "source", headerName: "RR", width: 80, sortable: false },
    { field: "status", headerName: "Status / Message", flex: 1, sortable: false },
    {
      field: "pickupTime",
      headerName: "Pickup Time",
      width: 180,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <>{params.value ? moment(params.value).format("MM/DD/YYYY HH:mm") : ""}</>
      ),
    },
    { field: "timeZone", headerName: "TZ", width: 80, sortable: false },
  ];

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (!(sortModel.length > 0)) return;
    setSearch({ ...search, sortAsc: sortModel[0].sort === "asc", offset: 0 });
    gridRef.current?.setPage(0);
  };

  const handlePaginationModel = (pagination: GridPaginationModel) => {
    if (pagination.pageSize !== search.pageSize) pagination.page = 0;
    setSearch({ ...search, pageSize: pagination.pageSize, offset: pagination.page });
  };

  const LoadingSkeleton = () => (
    <Box sx={{ height: "fit-content", padding: 0, margin: 0, overflow: "hidden" }}>
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} variant="rectangular" width="96%" sx={{ my: 1, ml: 2 }} />
      ))}
    </Box>
  );

  return (
    <Grid item xs={12}>
      <Box sx={{ mt: 2, overflow: "hidden" }}>
        <Paper sx={{ width: "99%", height: isLoading ? "100vh" : "auto", minHeight: 180, overflow: "hidden" }}>
          <DataGrid
            autoHeight={isLoading ? false : true}
            sx={{ overflow: "hidden", fontSize: ".95em", "--DataGrid-overlayHeight": "120px" }}
            rows={(rows || []).map((r, i) => ({ id: i, ...r }))}
            columns={columns}
            pagination
            sortingMode="server"
            paginationMode="server"
            onPaginationModelChange={handlePaginationModel}
            onSortModelChange={handleSortModelChange}
            initialState={{
              pagination: { paginationModel: { page: search.offset, pageSize: search.pageSize } },
              sorting: { sortModel: [{ field: "timeStamp", sort: "desc" }] },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            rowCount={total}
            disableColumnMenu
            slots={{
              loadingOverlay: LoadingSkeleton,
              noRowsOverlay: () => (
                <Stack height="100%" alignItems="center" justifyContent="center">
                  No B2B Activity
                </Stack>
              ),
            }}
            loading={isLoading}
            density="compact"
            apiRef={gridRef as any}
            getRowHeight={() => "auto"}
            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd")}
          />
        </Paper>
      </Box>
    </Grid>
  );
};

export default TripB2BActivity;
